import { RESTRICTED_ACTIONS } from "@/helpers/permissionConstants";

export const ADMIN_ROLES = ["system_admin", "business_unit"];
export const CLIENT_ROLE_GROUPS = new Set([
  "super_admin",
  "reseller",
  "solution_provider",
  "bank_admin",
  "sme_user",
]);
export const MODULE_TABLE_HEADERS = [
  "Module",
  "View",
  "Create",
  "Update",
  "Delete",
];

// Mirrors the backend's canManageAccountDates (userManagement.service.js):
// system_admin and super_admin may edit effectiveDate/expiryDate, for any
// target. Governs both fields — they're a matched start/end pair, so one
// actor rule covers both.
export const canEditAccountDates = (actorRole) =>
  actorRole === "system_admin" || actorRole === "super_admin";

// The backend only flips an expired account's status to "inactive" lazily —
// at login, on an authenticated request, or when an admin list/detail read
// triggers the sync. A user's data can sit in the frontend store from an
// earlier fetch and simply go stale as real time passes, with no action by
// anyone. Mirrors the backend's isExpiryDateExpired so the UI never shows
// "Active" next to an expiry date that has clearly already passed.
export const resolveDisplayStatus = (status, expiryDate) => {
  if (status !== "active" || !expiryDate) return status;
  return new Date(expiryDate).getTime() <= Date.now() ? "inactive" : status;
};

export const formatRoleLabel = (role = "") =>
  role
    .split("_")
    .map((part) =>
      part.toLowerCase() === "sme"
        ? "SME"
        : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join(" ");

export const formatPermissionLabel = (permission = "") =>
  permission
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const normalizeRoleKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

export const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (value._id) return normalizeId(value._id);
    if (value.id) return normalizeId(value.id);
    if (typeof value.toString === "function") return value.toString();
  }
  return String(value);
};

export const isAudienceAllowed = (audiences, audience) => {
  if (!audience) return true;
  const normalizedAudience = normalizeRoleKey(audience);
  if (!normalizedAudience) return true;
  if (!Array.isArray(audiences) || audiences.length === 0) return true;
  return audiences.map(normalizeRoleKey).includes(normalizedAudience);
};

export const filterModulesByAudience = (modules = [], audience) => {
  if (!audience) return modules;
  return (modules || []).reduce((acc, module) => {
    if (!module || typeof module !== "object") return acc;
    const moduleType = String(module.moduleType || "")
      .trim()
      .toLowerCase();
    const moduleAudiences = module.audiences;
    const subModules = Array.isArray(module.subModule) ? module.subModule : [];
    const filteredSubModules = subModules.filter((sub) => {
      const subAudiences =
        Array.isArray(sub?.audiences) && sub.audiences.length > 0
          ? sub.audiences
          : moduleAudiences;
      return isAudienceAllowed(subAudiences, audience);
    });
    const isModuleAllowed = isAudienceAllowed(moduleAudiences, audience);

    if (moduleType === "parent") {
      if (!isModuleAllowed || filteredSubModules.length === 0) return acc;
      acc.push({ ...module, subModule: filteredSubModules });
      return acc;
    }

    if (!isModuleAllowed) return acc;
    acc.push({ ...module, subModule: filteredSubModules });
    return acc;
  }, []);
};

export const filterModulesByCompanyFeatures = (
  modules = [],
  { customFrameworkEnabled = true } = {},
) => {
  if (customFrameworkEnabled !== false) return modules;

  const filterItems = (items = []) =>
    (items || []).reduce((acc, item) => {
      if (typeof item === "string") {
        if (normalizeRoleKey(item) !== "custom_framework") acc.push(item);
        return acc;
      }
      if (!item || typeof item !== "object") {
        acc.push(item);
        return acc;
      }

      const key = normalizeRoleKey(
        item.key || item.id || item._id || item.moduleName?.key,
      );
      if (key === "custom_framework") return acc;

      const nextItem = { ...item };
      if (Array.isArray(item.subModule)) {
        nextItem.subModule = filterItems(item.subModule);
      }
      if (Array.isArray(item.children)) {
        nextItem.children = filterItems(item.children);
      }
      acc.push(nextItem);
      return acc;
    }, []);

  return filterItems(modules);
};

export const buildPermitAllPermissions = (modules = [], audience) => {
  const source = filterModulesByAudience(modules, audience);
  const restrictionSet =
    audience === "user" ? RESTRICTED_ACTIONS.user || {} : null;
  const mapItem = (item = {}) => {
    const key = item.key || item.id || item._id || item.title || item.name;
    const title =
      item.title || item.name || item.value || item.key || "Permission";
    const subModules = Array.isArray(item.subModule) ? item.subModule : [];
    const restricted = new Set(restrictionSet?.[key] || []);
    return {
      key,
      title,
      actions: {
        create: !restricted.has("create"),
        update: !restricted.has("update"),
        view: !restricted.has("view"),
        delete: !restricted.has("delete"),
      },
      children: subModules.map(mapItem),
    };
  };

  return Array.isArray(source) ? source.map(mapItem) : [];
};

export const buildPermissionRows = (permissions = []) => {
  if (!Array.isArray(permissions) || permissions.length === 0) return [];

  const rows = [];
  const hasActions = (actions = {}) =>
    Object.values(actions || {}).some(Boolean);

  const traverse = (node, parents = []) => {
    if (typeof node === "string") {
      rows.push({
        key: node.key || node.id || node._id || "",
        label: [...parents, formatPermissionLabel(node)].join(" / "),
        actions: { create: true, update: true, view: true, delete: true },
      });
      return true;
    }

    if (!node || typeof node !== "object") return false;

    const title = formatPermissionLabel(
      node.title || node.name || node.value || node.key || "Module",
    );

    const path = [...parents, title];
    const children = Array.isArray(node.children)
      ? node.children
      : Array.isArray(node.subModule)
        ? node.subModule
        : [];

    let childHasPermission = false;
    children.forEach((child) => {
      childHasPermission = traverse(child, path) || childHasPermission;
    });

    const ownPermission = hasActions(node.actions) && children.length === 0;

    if (ownPermission) {
      rows.push({
        key: node.key || node.id || node._id || title,
        label: path.join(" / "),
        actions: {
          create: Boolean(node.actions?.create),
          update: Boolean(node.actions?.update),
          view: Boolean(node.actions?.view),
          delete: Boolean(node.actions?.delete),
        },
      });
    }

    return ownPermission || childHasPermission;
  };

  permissions.forEach((item) => traverse(item, []));
  return rows;
};

export const buildRoleItems = (
  roles = [],
  moduleSource = [],
  companyFeatures = {},
) => {
  if (!roles.length) return [];
  return roles.map((item) => {
    const role = item.name || item.title || item.key || "Unnamed role";
    const roleKey = normalizeRoleKey(
      item.key || item.name || item.title || item.role,
    );
    const roleGroup = normalizeRoleKey(item.group || roleKey);
    const audience = roleGroup;
    const permitAllPermissions = buildPermitAllPermissions(
      moduleSource,
      audience,
    );
    const isPermitAll = Boolean(item.isPermitAll) || roleKey === "super_admin";
    const basePermissions =
      isPermitAll && permitAllPermissions.length > 0
        ? permitAllPermissions
        : item.permissions;
    const featureFilteredPermissions = filterModulesByCompanyFeatures(
      basePermissions,
      companyFeatures,
    );
    const permissions = Array.isArray(featureFilteredPermissions)
      ? featureFilteredPermissions.map((perm) => {
          if (
            perm &&
            typeof perm === "object" &&
            (perm.actions || perm.children || perm.subModule)
          ) {
            return perm;
          }
          return perm?.title || perm?.key || perm?.name || "Permission";
        })
      : [];

    return {
      id: item._id || item.id || item.key || item.name || role,
      role,
      permissions,
      raw: item,
    };
  });
};
